import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Box, TablePagination } from "@mui/material";
import { IAllLabRequisitionsData } from "@home-health-notify/domain";
import { IAllLabRequisitionItem } from "@home-health-notify/models";
import LabRequisitionsTableItem from "./LabRequisitionsTableItem";
import ProgressLoader from "../loader/ProgressLoader";
import {
  ListingTableLayout,
  ListingTableContainer,
} from "../common/ListingTable.style";

interface Column {
  id:
    | "expand"
    | "sNo"
    | "patientName"
    | "labName"
    | "tests"
    | "externalOrderIds"
    | "status"
    | "createdDate";
  label: string;
  width?: number;
}

const columns: readonly Column[] = [
  { id: "expand", label: "", width: 40 },
  { id: "sNo", label: "No.", width: 50 },
  { id: "patientName", label: "Patient Name", width: 180 },
  { id: "labName", label: "Lab Name", width: 150 },
  { id: "tests", label: "Tests", width: 200 },
  { id: "externalOrderIds", label: "External Order IDs", width: 180 },
  { id: "status", label: "Status", width: 120 },
  { id: "createdDate", label: "Created Date", width: 150 },
];

type LabRequisitionsTableProps = {
  page: number;
  onSetPage: (value: number) => void;
  rowsPerPage: number;
  onPageLimitChange: (pageLimit: number) => void;
  labRequisitions: IAllLabRequisitionsData;
  isLoading: boolean;
  orgId: string;
};

export function LabRequisitionsTable({
  page,
  onSetPage,
  rowsPerPage,
  onPageLimitChange,
  labRequisitions,
  isLoading,
  orgId,
}: LabRequisitionsTableProps) {
  const handleChangePage = (event: unknown, newPage: number) => {
    onSetPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onPageLimitChange(+event.target.value);
  };

  return (
    <Box>
      <ListingTableLayout>
        <ListingTableContainer>
          <Table
            stickyHeader
            aria-label="lab requisitions table"
            data-testid="lab-requisitions-table"
          >
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.id}
                    style={{ width: column.width, padding: "10px" }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <ProgressLoader />
                  </TableCell>
                </TableRow>
              ) : labRequisitions.results.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    sx={{ textAlign: "center" }}
                  >
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                labRequisitions.results.map(
                  (requisition: IAllLabRequisitionItem, index: number) => (
                    <LabRequisitionsTableItem
                      key={requisition.id}
                      requisition={requisition}
                      number={index + 1 + rowsPerPage * page}
                      orgId={orgId}
                    />
                  ),
                )
              )}
            </TableBody>
          </Table>
        </ListingTableContainer>
        <TablePagination
          rowsPerPageOptions={[15, 25, 50, 100]}
          component="div"
          count={-1}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          slotProps={{
            actions: {
              nextButton: {
                disabled: !labRequisitions.nextCursor,
              },
            },
          }}
          labelDisplayedRows={({ from, to }) => `${from}–${to}`}
        />
      </ListingTableLayout>
    </Box>
  );
}
